import React, { useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { Box, Button, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyIcon from "@mui/icons-material/Key";
import PropTypes from "prop-types";

import BaseTable from "Components/Common/BaseTable";
import BreadCrumb from "Components/Common/BreadCrumb";
import Backdrop from "Components/Common/Backdrop";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import Tooltip from "Components/Common/Tooltip";
import UserModal from "./UserModal";
import ChangePasswordModal from "./ChangePasswordModal";

import { useDeleteUserMutation, useGetUsersQuery } from "apis/User";

const RECORDS_PER_PAGE = 10;

const User = ({ t }) => {
  const [userId, setUserId] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [emailForChangePasswordModal, setEmailForChangePasswordModal] =
    useState("");
  const [deleteUserId, setDeleteUserId] = useState("");

  const [deleteUser, { isLoading }] = useDeleteUserMutation();

  const {
    data: { data: userData } = {},
    isLoading: isUserDataLoading,
    isFetching: isUserDataFetching,
  } = useGetUsersQuery();

  const columns = useMemo(
    () => [
      { title: "First Name", field: "firstName" },
      { title: "Last Name", field: "lastName" },
      { title: "User Name", field: "userName" },
      { title: "Email", field: "email" },

      {
        title: "Utility & Roles",
        columnProps: { style: { minWidth: "350px" } },
        renderCell: ({ utilityRoles }) => (
          <>
            {utilityRoles.map(({ roles, utilityName }, index) => (
              <div className="d-flex gap-1" key={index}>
                {!!utilityName && (
                  <Typography variant="body2">{utilityName}</Typography>
                )}
                {!!utilityName && "-"}
                <Typography variant="body2" key={index}>
                  {roles.map(({ name }) => name).join(", ")}
                </Typography>
              </div>
            ))}
          </>
        ),
      },
      {
        title: "Actions",
        renderCell: ({ id, email, utilityRoles }) => {
          const isNotSuperAdmin = utilityRoles.every(({ roles }) =>
            roles.every(({ name }) => name !== "Super Admin")
          );
          return (
            <Box sx={{ minWidth: "110px" }}>
              {isNotSuperAdmin && (
                <Tooltip title={t("Edit user")}>
                  <IconButton
                    onClick={() => {
                      setUserId(id);
                      setIsOpen(true);
                    }}
                  >
                    <EditIcon color="primary" fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={t("Change Password")}>
                <IconButton
                  onClick={() => setEmailForChangePasswordModal(email)}
                >
                  <KeyIcon color="primary" fontSize="small" />
                </IconButton>
              </Tooltip>
              {isNotSuperAdmin && (
                <Tooltip title={t("Delete user")}>
                  <IconButton onClick={() => setDeleteUserId(id)}>
                    <DeleteIcon color="primary" fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ],
    []
  );

  const handleDelete = useMemo(
    () => async () => {
      try {
        await deleteUser(deleteUserId);
        setDeleteUserId("");
      } catch (err) {}
    },
    [deleteUser]
  );

  return (
    <div className="page-content h-100">
      <Backdrop open={isUserDataLoading || isUserDataFetching} />
      {isOpen && (
        <UserModal
          handleClose={() => {
            setIsOpen(false);
            setUserId("");
          }}
          isOpen={isOpen}
          userId={userId}
        />
      )}
      {!!emailForChangePasswordModal && (
        <ChangePasswordModal
          handleClose={() => {
            setEmailForChangePasswordModal("");
          }}
          userEmail={emailForChangePasswordModal}
        />
      )}
      <ConfirmationDialog
        isLoading={isLoading}
        isOpen={!!deleteUserId}
        onCancel={() => setDeleteUserId("")}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this user?"
        variant="warning"
        title="Warning"
      />
      <BreadCrumb title={t("User Management")} pageTitle={t("User")} />
      <div className="text-end mb-2">
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsOpen(true)}
        >
          {t("Add User")}
        </Button>
      </div>

      <BaseTable
        data={userData ?? []}
        columns={columns}
        pageSize={RECORDS_PER_PAGE}
        maxHeight="calc(100vh - 200px)"
      />
    </div>
  );
};

User.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(User);
